import { useEffect, useMemo, useState } from 'react';
import {
  getCustomerPolicy,
  getGithubAppStatus,
  updateCustomerPolicy,
  type CustomerDeploymentStatus,
  type CustomerPolicy,
  type GithubInstallation,
} from './api.js';

const organizationPattern = /^[A-Za-z0-9](?:[A-Za-z0-9_.-]*[A-Za-z0-9])?$/u;

export function CustomerPolicyEditor({
  installation,
  onDirtyChange,
}: {
  installation: CustomerDeploymentStatus;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const [policy, setPolicy] = useState<CustomerPolicy | null>(null);
  const [githubInstallations, setGithubInstallations] = useState<GithubInstallation[]>([]);
  const [githubInstallationId, setGithubInstallationId] = useState('');
  const [allRepositories, setAllRepositories] = useState(true);
  const [repositories, setRepositories] = useState('');
  const [allowFetch, setAllowFetch] = useState(true);
  const [allowPush, setAllowPush] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedGithubInstallation = githubInstallations.find(
    (value) => value.installationId === githubInstallationId,
  );
  const normalizedOrganization = selectedGithubInstallation?.accountLogin?.trim() ?? '';
  const repositoryList = useMemo(() =>
    [...new Set(repositories
      .split(/[\s,]+/u)
      .map((value) => value.trim())
      .filter(Boolean))],
  [repositories]);
  const selectedOperations = useMemo(() => [
    ...(allowFetch ? ['fetch' as const] : []),
    ...(allowPush ? ['push' as const] : []),
  ], [allowFetch, allowPush]);

  useEffect(() => {
    let current = true;
    void Promise.all([
      getCustomerPolicy(installation.installationId),
      getGithubAppStatus(),
    ])
      .then(([value, github]) => {
        if (!current) return;
        setGithubInstallations(github.installations);
        setPolicy(value);
        if (value) {
          applyPolicy(value);
        } else if (github.installations.length === 1) {
          setGithubInstallationId(github.installations[0]!.installationId);
        }
      })
      .catch((reason: Error) => current && setError(reason.message))
      .finally(() => current && setLoading(false));
    return () => { current = false; };

    function applyPolicy(value: CustomerPolicy) {
      setGithubInstallationId(value.githubInstallationId ?? '');
      setAllRepositories(value.allRepositories);
      setRepositories(value.repositories.join('\n'));
      setAllowFetch(value.operations.includes('fetch'));
      setAllowPush(value.operations.includes('push'));
    }
  }, [installation.installationId]);

  useEffect(() => {
    if (policy?.status !== 'deploying') return;
    let current = true;
    const timer = window.setInterval(() => {
      void getCustomerPolicy(installation.installationId)
        .then((value) => {
          if (current && value) setPolicy(value);
        })
        .catch(() => {
          // The last known deployment state remains useful while the control plane retries.
        });
    }, 5_000);
    return () => {
      current = false;
      window.clearInterval(timer);
    };
  }, [installation.installationId, policy?.status]);

  const liveStatus = installation.policyRevision === policy?.revision
    ? installation.policyStatus ?? policy?.status
    : policy?.status;
  const deploymentInProgress = liveStatus === 'pending' || liveStatus === 'deploying';
  const policyRepositories = [...(policy?.repositories ?? [])].sort();
  const currentRepositories = [...repositoryList].sort();
  const hasChanges = !policy
    || githubInstallationId !== policy.githubInstallationId
    || allRepositories !== policy.allRepositories
    || (!allRepositories && currentRepositories.join('\n') !== policyRepositories.join('\n'))
    || selectedOperations.join(',') !== policy.operations.join(',');
  const mayRetry = liveStatus === 'failed';
  const valid = Boolean(selectedGithubInstallation)
    && organizationPattern.test(normalizedOrganization)
    && (allRepositories || repositoryList.length > 0)
    && selectedOperations.length > 0;
  const canSave = valid
    && !saving
    && !deploymentInProgress
    && (hasChanges || mayRetry);
  const validationMessage = !selectedGithubInstallation
    ? 'Install or select the GitHub App to continue.'
    : !organizationPattern.test(normalizedOrganization)
      ? 'Use a valid GitHub organization name.'
      : !allRepositories && repositoryList.length === 0
        ? 'Add at least one repository to this tighter scope.'
        : selectedOperations.length === 0
          ? 'Choose at least one allowed Git action.'
          : null;

  useEffect(() => {
    onDirtyChange?.(touched && hasChanges && !saving && !deploymentInProgress);
  }, [deploymentInProgress, hasChanges, onDirtyChange, saving, touched]);

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const next = await updateCustomerPolicy(installation.installationId, {
        expectedRevision: policy?.revision ?? 0,
        githubInstallationId,
        allRepositories,
        repositories: allRepositories ? [] : repositoryList,
        operations: selectedOperations,
      });
      setPolicy(next);
      setTouched(false);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="policy-loading" role="status">
        <span className="deployment-spinner dark" />
        <span><strong>Loading repository access</strong><small>Reading the latest policy state…</small></span>
      </div>
    );
  }

  const repositorySummary = allRepositories
    ? `Every App-authorized repository in ${normalizedOrganization || 'the organization'}`
    : repositoryList.length === 1
      ? `${normalizedOrganization || 'organization'}/${repositoryList[0]}`
      : `${repositoryList.length} selected repositories`;

  return (
    <section className="policy-editor" aria-label="GitHub access policy">
      <div className="policy-workspace">
        <div className="policy-builder">
          {error && <div className="deployment-warning error" role="alert">{error}</div>}

          <section className="policy-step" aria-labelledby="policy-organization-title">
            <div className="policy-step-number" aria-hidden="true">1</div>
            <div className="policy-step-body">
              <div className="policy-step-heading">
                <div>
                  <h3 id="policy-organization-title">Which GitHub App installation?</h3>
                  <p>The installed account becomes the enforced repository owner boundary.</p>
                </div>
                {normalizedOrganization && <code>github.com/{normalizedOrganization}/…</code>}
              </div>
              {githubInstallations.length > 0 ? (
                <label htmlFor="policy-github-installation">
                  <span>Installed account</span>
                  <select
                    id="policy-github-installation"
                    aria-label="GitHub App installation"
                    required
                    value={githubInstallationId}
                    onChange={(event) => {
                      setGithubInstallationId(event.target.value);
                      setTouched(true);
                    }}
                  >
                    <option value="">Select an installation</option>
                    {githubInstallations.map((value) => (
                      <option key={value.installationId} value={value.installationId}>
                        {value.accountLogin ?? `Installation ${value.installationId}`}
                        {value.accountType ? ` · ${value.accountType}` : ''}
                      </option>
                    ))}
                  </select>
                  <small id="policy-organization-help">
                    GitHub is connected. Activating below binds this account and repository
                    scope to the selected AWS environment.
                  </small>
                  <a className="policy-aws-link" href="/auth/github/install">
                    Install the App for another GitHub account <span aria-hidden="true">↗</span>
                  </a>
                </label>
              ) : (
                <div className="deployment-warning" role="status">
                  <span>
                    Install the Cyrus GitHub App before configuring repository access.
                  </span>
                  <a href="/auth/github/install">Install GitHub App</a>
                </div>
              )}
            </div>
          </section>

          <section className="policy-step" aria-labelledby="policy-repositories-title">
            <div className="policy-step-number" aria-hidden="true">2</div>
            <div className="policy-step-body">
              <div className="policy-step-heading">
                <div>
                  <h3 id="policy-repositories-title">Which repositories?</h3>
                  <p>Choose whether future repositories should be included automatically.</p>
                </div>
              </div>
              <fieldset className="policy-scope">
                <legend className="sr-only">Repository scope</legend>
                <label className={allRepositories ? 'selected' : ''}>
                  <input
                    type="radio"
                    name="repository-scope"
                    checked={allRepositories}
                    onChange={() => {
                      setAllRepositories(true);
                      setTouched(true);
                    }}
                  />
                  <span>
                    <strong>All repositories</strong>
                    <small>
                      Includes current and future repositories only when the GitHub App
                      installation grants all repositories.
                    </small>
                  </span>
                  <i aria-hidden="true">Broadest</i>
                </label>
                <label className={!allRepositories ? 'selected' : ''}>
                  <input
                    type="radio"
                    name="repository-scope"
                    checked={!allRepositories}
                    onChange={() => {
                      setAllRepositories(false);
                      setTouched(true);
                    }}
                  />
                  <span>
                    <strong>Only selected repositories</strong>
                    <small>Access stays limited to the exact names you provide.</small>
                  </span>
                  <i aria-hidden="true">Tighter</i>
                </label>
                {!allRepositories && (
                  <label className="policy-repository-list" htmlFor="policy-repositories">
                    <span>Repository names</span>
                    <textarea
                      id="policy-repositories"
                      aria-label="Repository names"
                      aria-describedby="policy-repositories-help"
                      rows={4}
                      placeholder={'backend-api\ninfrastructure'}
                      value={repositories}
                      onChange={(event) => {
                        setRepositories(event.target.value);
                        setTouched(true);
                      }}
                    />
                    <small
                      id="policy-repositories-help"
                      className={!repositoryList.length ? 'policy-field-error' : ''}
                    >
                      {!repositoryList.length
                        ? 'Add at least one repository name. Do not include the organization or full URL.'
                        : 'One name per line. Do not include the organization or full URL.'}
                    </small>
                  </label>
                )}
              </fieldset>
            </div>
          </section>

          <section className="policy-step" aria-labelledby="policy-actions-title">
            <div className="policy-step-number" aria-hidden="true">3</div>
            <div className="policy-step-body">
              <div className="policy-step-heading">
                <div>
                  <h3 id="policy-actions-title">What can a desktop do?</h3>
                  <p>Start read-only. Enable write access only for workflows that need it.</p>
                </div>
              </div>
              <fieldset className="policy-operations">
                <legend className="sr-only">Allowed Git operations</legend>
                <label className={allowFetch ? 'selected' : ''}>
                  <input
                    type="checkbox"
                    checked={allowFetch}
                    onChange={(event) => {
                      setAllowFetch(event.target.checked);
                      setTouched(true);
                    }}
                  />
                  <span>
                    <strong>Clone and fetch</strong>
                    <small>Read branches, commits, and repository contents.</small>
                  </span>
                  <i aria-hidden="true">Read</i>
                </label>
                <label className={allowPush ? 'selected sensitive' : ''}>
                  <input
                    type="checkbox"
                    checked={allowPush}
                    onChange={(event) => {
                      setAllowPush(event.target.checked);
                      setTouched(true);
                    }}
                  />
                  <span>
                    <strong>Push changes</strong>
                    <small>Create or update branches and tags through Git.</small>
                  </span>
                  <i aria-hidden="true">Write</i>
                </label>
              </fieldset>
            </div>
          </section>

          <section className="policy-step" aria-labelledby="policy-credential-title">
            <div className="policy-step-number" aria-hidden="true">4</div>
            <div className="policy-step-body">
              <div className="policy-step-heading">
                <div>
                  <h3 id="policy-credential-title">Managed short-lived credential</h3>
                  <p>
                    The customer-owned connector renews the GitHub installation token before it expires.
                  </p>
                </div>
                <span className="policy-write-only">Automatic</span>
              </div>
              <div className="policy-token" aria-label="Managed GitHub credential">
                <span>Delivery path</span>
                <strong>GitHub App → customer connector → customer Secrets Manager</strong>
                <small>
                  Tokens expire after one hour. Refresh runs in the customer VPC and the token is
                  never sent to the browser or MicroVM.
                </small>
              </div>
            </div>
          </section>

          <footer className="policy-actions">
            <div>
              <strong>{policy ? `Revision ${policy.revision}` : 'First policy'}</strong>
              <small id="policy-save-help" className={validationMessage ? 'policy-field-error' : ''}>
                {validationMessage
                  ?? (deploymentInProgress
                  ? 'Your VPC connector is pulling and applying this revision in AWS.'
                  : hasChanges
                    ? policy
                      ? 'This sends the updated boundary to your VPC connector for validation and activation.'
                      : 'This activates short-lived GitHub credentials at your VPC egress boundary.'
                    : 'The live policy already matches these settings.')}
              </small>
            </div>
            <button
              className="deployment-primary"
              type="button"
              aria-describedby="policy-save-help"
              disabled={!canSave}
              onClick={() => void save()}
            >
              {saving
                  ? <><span className="deployment-spinner" />Sending to AWS…</>
                : deploymentInProgress
                  ? 'Applying changes…'
                  : mayRetry && !hasChanges
                    ? 'Retry activation'
                    : policy
                      ? hasChanges ? 'Apply update in AWS' : 'No changes to save'
                      : 'Activate GitHub access in AWS'}
            </button>
          </footer>
        </div>

        <aside className="policy-summary" aria-label="Policy outcome">
          <header>
            <span className="eyebrow">Policy outcome</span>
            <PolicyState status={liveStatus} revision={policy?.revision} />
          </header>
          <h3>What will happen</h3>
          <div className="policy-destination">
            <span>Approved destination</span>
            <code>github.com/{normalizedOrganization || 'organization'}/…</code>
          </div>
          <ol>
            <li className="allow">
              <i aria-hidden="true">✓</i>
              <div>
                <strong>Repository must match</strong>
                <span>{repositorySummary}</span>
              </div>
            </li>
            <li className="allow">
              <i aria-hidden="true">✓</i>
              <div>
                <strong>{allowFetch ? 'Clone and fetch allowed' : 'Clone and fetch blocked'}</strong>
                <span>{allowFetch ? 'Read operations receive broker authentication.' : 'Read operations are denied.'}</span>
              </div>
            </li>
            <li className={allowPush ? 'caution' : 'deny'}>
              <i aria-hidden="true">{allowPush ? '!' : '×'}</i>
              <div>
                <strong>{allowPush ? 'Push allowed' : 'Push blocked'}</strong>
                <span>{allowPush ? 'Matching desktops can write through Git.' : 'No Git write operations can pass.'}</span>
              </div>
            </li>
            <li className="deny">
              <i aria-hidden="true">×</i>
              <div>
                <strong>Everything else denied</strong>
                <span>Other organizations, repositories, and operations fail closed.</span>
              </div>
            </li>
          </ol>
          <div className="policy-secret-boundary">
            <span aria-hidden="true">⌁</span>
            <div>
              <strong>Credential lands only at the VPC egress boundary</strong>
              <small>The browser and MicroVM never receive the installation token.</small>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}

function PolicyState({
  status,
  revision,
}: {
  status: CustomerDeploymentStatus['policyStatus'] | CustomerPolicy['status'] | undefined;
  revision: number | undefined;
}) {
  return (
    <span className={`policy-state ${status ?? 'draft'}`}>
      <i />
      {status === 'active'
        ? `Live · r${revision}`
        : status === 'failed'
          ? 'Needs attention'
          : status === 'pending'
            ? `Waiting for AWS${revision ? ` · r${revision}` : ''}`
          : status === 'deploying'
            ? `Applying${revision ? ` · r${revision}` : ''}`
            : 'Not active'}
    </span>
  );
}
